import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';
import TiktokPostSettings from './TiktokPostSettings';
import TiktokPreviewConfirm from './TiktokPreviewConfirm';

// Video panjang ke TikTok & Instagram (2026-10-26). Dipasang di halaman UGC di bawah pemilih channel YouTube.
// Pilihan disimpan otomatis ke server (routes/ugc-sosial.js) dan ikut terkirim saat tombol Setujui diklik
// (jam sama dengan YouTube), atau langsung lewat tombol "Kirim ke TikTok/IG sekarang".
// Syarat: TikTok ≤ batas durasi akun (creator_info), Instagram 3 dtk–15 mnt & ≤ 300 MB.
// Pengaturan TikTok WAJIB dipilih user (privacy, consent, pengungkapan komersial) — Content Posting Guidelines.
const API = process.env.REACT_APP_API_URL;
const hdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('sm_token')}` } });
const TERKUNCI = ['scheduled', 'uploading', 'done'];
const STATUS = {
  pending: ['Dipilih', colors.textSecondary], scheduled: ['Terjadwal', colors.warning], uploading: ['Mengunggah…', colors.accentText],
  done: ['Terkirim', colors.success], failed: ['Gagal', colors.danger],
};
const lengkapTt = s => !!(s && s.privacy_level && s.consent && (!s.is_commercial || s.is_your_brand || s.is_branded_content) && !(s.is_branded_content && s.privacy_level === 'SELF_ONLY'));
const kotak = { background: colors.bgCardMuted, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 14, marginTop: 18 };

export default function SosialUgc({ projectId, data }) {
  const [info, setInfo] = useState(null), [err, setErr] = useState(''), [simpan, setSimpan] = useState(null);
  const [selTt, setSelTt] = useState([]), [ttSet, setTtSet] = useState({}), [selIg, setSelIg] = useState([]), [caption, setCaption] = useState('');
  const [caps, setCaps] = useState({}), [kirim, setKirim] = useState(false), [picu, setPicu] = useState(0);
  const siap = useRef(false), timer = useRef(null);

  const muat = useCallback(async (awal) => {
    try {
      const { data: d } = await axios.get(`${API}/api/ugc-sosial/${projectId}`, hdr());
      setInfo(d);
      if (awal) {
        const tt = d.tiktok.dipilih.filter(r => !TERKUNCI.includes(r.status)), ig = d.instagram.dipilih.filter(r => !TERKUNCI.includes(r.status));
        const adaPilihan = d.tiktok.dipilih.length || d.instagram.dipilih.length;
        // Belum pernah dipilih: usulkan akun yang bahasa targetnya sama dengan video (TikTok tetap perlu diatur user).
        const usulTt = adaPilihan ? tt.map(r => r.tiktok_account_id) : d.tiktok.akun.filter(a => a.cocok_bahasa && (!a.alasan || !a.caps_max_duration_sec)).map(a => a.id);
        const usulIg = adaPilihan ? ig.map(r => r.ig_account_id) : d.instagram.akun.filter(a => a.cocok_bahasa && !a.alasan).map(a => a.id);
        setSelTt(usulTt); setSelIg(usulIg);
        const s = {};
        tt.forEach(r => { s[r.tiktok_account_id] = { privacy_level: r.privacy_level, allow_comment: !r.disable_comment, allow_duet: !r.disable_duet, allow_stitch: !r.disable_stitch,
          is_commercial: r.is_commercial, is_your_brand: r.is_your_brand, is_branded_content: r.is_branded_content, consent: true }; });
        setTtSet(s);
        setCaption((d.tiktok.dipilih[0] || d.instagram.dipilih[0] || {}).caption || d.judul || '');
        // Usulan bahasa langsung disimpan (Instagram langsung ikut; TikTok menunggu pengaturan lengkap).
        setTimeout(() => { siap.current = true; if (!adaPilihan && (usulTt.length || usulIg.length)) setPicu(x => x + 1); }, 0);
      }
    } catch (e) { setErr(e.response?.data?.error || 'Tujuan TikTok/Instagram tidak bisa dimuat'); }
  }, [projectId]);
  useEffect(() => { siap.current = false; muat(true); }, [muat, data && data.status]);

  // Pantau status selama ada yang terjadwal / sedang diunggah.
  const berjalan = info && [...info.tiktok.dipilih, ...info.instagram.dipilih].some(r => ['scheduled', 'uploading'].includes(r.status));
  useEffect(() => { if (!berjalan) return undefined; const t = setInterval(() => muat(false), 15000); return () => clearInterval(t); }, [berjalan, muat]);

  const akunTt = info ? info.tiktok.akun : [], akunIg = info ? info.instagram.akun : [];
  const terkunciTt = info ? info.tiktok.dipilih.filter(r => TERKUNCI.includes(r.status)).map(r => r.tiktok_account_id) : [];
  const terkunciIg = info ? info.instagram.dipilih.filter(r => TERKUNCI.includes(r.status)).map(r => r.ig_account_id) : [];
  const ttTerpilih = akunTt.filter(a => selTt.includes(a.id));
  const ttBelum = ttTerpilih.filter(a => !lengkapTt(ttSet[a.id]));

  // Simpan otomatis (0,9 dtk setelah perubahan terakhir): hanya TikTok yang pengaturannya lengkap.
  useEffect(() => {
    if (!siap.current || !info) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSimpan('menyimpan');
      try {
        const tiktok = ttTerpilih.filter(a => lengkapTt(ttSet[a.id])).map(a => { const s = ttSet[a.id];
          return { tiktok_account_id: a.id, privacy_level: s.privacy_level, disable_comment: !s.allow_comment, disable_duet: !s.allow_duet, disable_stitch: !s.allow_stitch,
            is_commercial: !!s.is_commercial, is_your_brand: !!s.is_your_brand, is_branded_content: !!s.is_branded_content, consent: !!s.consent }; });
        await axios.put(`${API}/api/ugc-sosial/${projectId}`, { caption, tiktok, instagram: selIg }, hdr());
        setSimpan('ok'); setErr(''); muat(false);
      } catch (e) { setSimpan(null); setErr(e.response?.data?.error || 'Gagal menyimpan tujuan TikTok/Instagram'); }
    }, 900);
    return () => clearTimeout(timer.current);
  }, [selTt, ttSet, selIg, caption, picu]); // eslint-disable-line

  // Batas durasi akun baru dimuat (creator_info) → hitung ulang kelayakan di server.
  const capsKunci = Object.keys(caps).join(',');
  useEffect(() => { if (capsKunci) muat(false); }, [capsKunci, muat]);

  if (!info) return err ? <div style={{ ...kotak, fontSize: 12.5, color: colors.danger }}>{err}</div> : null;
  if (!info.ada_video || ((!info.tiktok.aktif || !akunTt.length) && (!info.instagram.aktif || !akunIg.length))) return null;

  const pending = [...info.tiktok.dipilih, ...info.instagram.dipilih].filter(r => r.status === 'pending').length;
  const kirimSekarang = async () => {
    setKirim(true); setErr('');
    try { await axios.post(`${API}/api/ugc-sosial/${projectId}/kirim`, {}, hdr()); await muat(false); }
    catch (e) { setErr(e.response?.data?.error || 'Gagal mengirim'); }
    setKirim(false);
  };
  const baris = (a, jenis, on, ubah) => {
    const kunci = (jenis === 'tt' ? terkunciTt : terkunciIg).includes(a.id);
    const row = (jenis === 'tt' ? info.tiktok.dipilih.find(r => r.tiktok_account_id === a.id) : info.instagram.dipilih.find(r => r.ig_account_id === a.id));
    // TikTok yang batas durasinya belum dimuat tetap boleh dicentang supaya creator_info-nya dimuat.
    const blok = !!a.alasan && !(jenis === 'tt' && !a.caps_max_duration_sec && !a.needs_reconnect);
    const st = row && STATUS[row.status];
    return (
      <div key={a.id} style={{ padding: '7px 10px', borderRadius: 8, background: colors.bgCard, border: `1px solid ${colors.border}`, marginBottom: 6, fontSize: 12.5 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: kunci || blok ? 'default' : 'pointer' }}>
          <input type="checkbox" style={{ width: 15, height: 15, margin: 0 }} checked={kunci || on} disabled={kunci || (blok && !on)} onChange={e => ubah(e.target.checked)} />
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{a.display_name || a.username}{a.username && a.display_name ? <span style={{ color: colors.textSecondary }}> @{a.username}</span> : null}</span>
          {a.cocok_bahasa && <span style={{ fontSize: 10.5, fontWeight: 700, color: colors.accentText, background: colors.accentMuted, borderRadius: 6, padding: '2px 6px' }}>{String(a.target_lang).toUpperCase()} cocok</span>}
          {st && <span style={{ fontSize: 11, fontWeight: 600, color: st[1] }}>{st[0]}</span>}
        </label>
        {blok && <div style={{ fontSize: 11.5, color: colors.textSecondary, marginTop: 3, marginLeft: 23 }}>{a.alasan}</div>}
        {row && row.status === 'failed' && row.error_message && <div style={{ fontSize: 11.5, color: colors.danger, marginTop: 3, marginLeft: 23 }}>{row.error_message} — centang lagi untuk mencoba ulang.</div>}
        {row && row.status === 'scheduled' && row.scheduled_at && <div style={{ fontSize: 11.5, color: colors.textSecondary, marginTop: 3, marginLeft: 23 }}>Dikirim {new Date(row.scheduled_at).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}</div>}
        {row && row.permalink && <a href={row.permalink} target="_blank" rel="noreferrer" style={{ fontSize: 11.5, marginLeft: 23 }}>Lihat di Instagram</a>}
        {jenis === 'tt' && row && row.status === 'uploading' && <div style={{ fontSize: 11.5, color: colors.textSecondary, marginTop: 3, marginLeft: 23 }}>Video panjang butuh waktu diproses TikTok (bisa beberapa menit).</div>}
      </div>
    );
  };

  return (
    <div style={kotak}>
      <div style={{ fontSize: 13, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
        <i className="ti ti-share" /> Kirim juga ke TikTok & Instagram <span style={{ fontWeight: 400, color: colors.textSecondary }}>(opsional)</span>
      </div>
      <div style={{ fontSize: 12, color: colors.textSecondary, margin: '4px 0 10px' }}>
        Video utuh {Math.floor(info.durasi / 60)}:{String(info.durasi % 60).padStart(2, '0')} · {info.ukuran_mb} MB. Instagram maks 15 menit & 300 MB; TikTok mengikuti batas durasi tiap akun. Ikut terkirim saat kamu klik Setujui di bawah (jam sama dengan YouTube).
      </div>
      {info.tiktok.aktif && akunTt.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}><i className="ti ti-brand-tiktok" /> TikTok</div>
          {akunTt.map(a => baris(a, 'tt', selTt.includes(a.id), on => setSelTt(p => (on ? [...p, a.id] : p.filter(x => x !== a.id)))))}
          {ttTerpilih.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <TiktokPostSettings accounts={ttTerpilih} settings={ttSet} onChange={setTtSet} onCaps={setCaps} tanpaJadwal />
              <TiktokPreviewConfirm accounts={ttTerpilih} settings={ttSet} caps={caps} caption={caption || data.title} thumbnailUrl={data.thumbnail_url ? `${API}${data.thumbnail_url}` : null} />
            </div>
          )}
          {ttBelum.length > 0 && <div style={{ fontSize: 12, color: colors.warning, marginTop: 4 }}>Lengkapi pengaturan TikTok untuk {ttBelum.map(a => a.display_name || a.username).join(', ')} — belum ikut dikirim sampai lengkap.</div>}
        </div>
      )}
      {info.instagram.aktif && akunIg.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}><i className="ti ti-brand-instagram" /> Instagram (Reels)</div>
          {akunIg.map(a => baris(a, 'ig', selIg.includes(a.id), on => setSelIg(p => (on ? [...p, a.id] : p.filter(x => x !== a.id)))))}
        </div>
      )}
      {(selTt.length > 0 || selIg.length > 0) && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>Caption TikTok & Instagram ({caption.length}/2200)</div>
          <textarea value={caption} maxLength={2200} onChange={e => setCaption(e.target.value)} style={{ width: '100%', minHeight: 70, boxSizing: 'border-box', fontSize: 12.5 }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {pending > 0 && (
          <button type="button" className="btn btn-outline" disabled={kirim} onClick={kirimSekarang} style={{ fontSize: 12.5 }}>
            {kirim ? 'Mengirim…' : `Kirim ke TikTok/IG sekarang (${pending})`}
          </button>
        )}
        <span style={{ fontSize: 11.5, color: err ? colors.danger : colors.textSecondary }}>
          {err || (simpan === 'menyimpan' ? 'Menyimpan pilihan…' : simpan === 'ok' ? `Tersimpan ✓ ${pending ? `· ${pending} tujuan menunggu Setujui / Kirim` : ''}` : '')}
        </span>
      </div>
    </div>
  );
}
